import { useEffect, useState } from "react";

// Question definitions. Edit text/options/myAnswer here freely.
// Types: "text" (one line), "long" (textarea), "choice" (multiple choice), "song" (URL with embed)
const QUESTIONS = [
  {
    id: "q1",
    type: "text",
    prompt:
      "if you could master one weirdly specific skill overnight, what is it?",
    placeholder: "be honest, be unhinged",
    myAnswer:
      "falling asleep immediately whenever i want. i stay up way too late watching tiktok and reels...",
  },
  {
    id: "q2",
    type: "long",
    prompt:
      "what's something people misunderstand about you that you secretly wish they got right?",
    placeholder: "i'll go after you do, promise",
    myAnswer:
      "i'm autistic. i have no object permanance so i WILL forget to reply but it doesn't mean i don't care or do it on purpose...",
  },
  {
    id: "q3",
    type: "choice",
    prompt: "the ideal saturday morning is:",
    options: [
      "still asleep, please don't",
      "waking up early to see the sunrise on the beach",
      "going to sleep at 6am after gaming all night",
      "in bed with someone who makes me laugh",
    ],
    myAnswer: "going to sleep at 6am after gaming all night",
  },

  // ===== Music block: 5 scenarios =====
  {
    id: "song1",
    type: "song",
    prompt:
      "song round — 1 of 5: a song that you would play at my hamster's funeral so i laugh instead of cry.",
    placeholder: "spotify or youtube link…",
    myAnswer: "https://open.spotify.com/track/4cOdK2wGLETKBW3PvgPWqT",
  },
  {
    id: "song2",
    type: "song",
    prompt:
      "song round — 2 of 5: a song that goes hard but you're embarrassed to admit you know every word.",
    placeholder: "no judgment. (i'm judging a little.)",
    myAnswer:
      "https://open.spotify.com/track/45hOioMDJktr86iKDHC8gr?si=1575df50555b4d4a",
  },
  {
    id: "song3",
    type: "song",
    prompt:
      "song round — 3 of 5: a song you'd put on right after winning a fistfight in a waffle house parking lot.",
    placeholder: "the fight was over honor. probably.",
    myAnswer:
      "https://open.spotify.com/track/7CpbhqKUedOIrcvc94p60Y?si=c29b43c994e94977",
  },
  {
    id: "song4",
    type: "song",
    prompt:
      "song round — 4 of 5: a song that immediately makes you feel 17 again.",
    placeholder: "the embarrassing kind of nostalgia",
    myAnswer:
      "https://open.spotify.com/track/3DamFFqW32WihKkTVlwTYQ?si=252f997a55274761",
  },
  {
    id: "song5",
    type: "song",
    prompt:
      "song round — 5 of 5: a song you'd play on a slow drive together with no destination.",
    placeholder: "stakes are high. choose wisely.",
    myAnswer:
      "https://open.spotify.com/track/7K3Lc3TfSR14aTOjIH7TUj?si=34c57ff0845b44bd",
  },
  // ===== End music block =====

  {
    id: "q9",
    type: "text",
    prompt:
      "tell me one tiny thing about you nobody on your social medias would know.",
    placeholder: "save the rest for saturday",
    myAnswer:
      "i am scared of the ocean. i've had nightmares about drowning or getting eaten by a giant squid since I was little.",
  },
];

// ---------- helpers for song embeds ----------
function parseMediaUrl(url) {
  if (!url) return null;
  const trimmed = url.trim();
  const sp = trimmed.match(
    /open\.spotify\.com\/(?:intl-[a-z]+\/)?(track|album|playlist|episode)\/([a-zA-Z0-9]+)/
  );
  if (sp) return { type: "spotify", kind: sp[1], id: sp[2] };
  const yt =
    trimmed.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/) ||
    trimmed.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]{6,})/) ||
    trimmed.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/);
  if (yt) return { type: "youtube", id: yt[1] };
  return null;
}

function MediaEmbed({ url, compact }) {
  const parsed = parseMediaUrl(url);
  if (!parsed) {
    return url ? (
      <p className="tagline" style={{ fontSize: "0.9rem", marginTop: 8 }}>
        hmm, that link doesn't look like a spotify or youtube one. paste the
        full url and i'll show it here.
      </p>
    ) : null;
  }
  const h = compact ? 80 : parsed.type === "youtube" ? 240 : 152;
  if (parsed.type === "spotify") {
    return (
      <iframe
        title="spotify embed"
        src={`https://open.spotify.com/embed/${parsed.kind}/${parsed.id}`}
        width="100%"
        height={h}
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        loading="lazy"
        style={{ borderRadius: 8, marginTop: 12 }}
      />
    );
  }
  return (
    <iframe
      title="youtube embed"
      src={`https://www.youtube.com/embed/${parsed.id}`}
      width="100%"
      height={h}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      loading="lazy"
      style={{ borderRadius: 8, marginTop: 12 }}
    />
  );
}

// ---------- main scene ----------
export default function Questionnaire({ session, update, goTo }) {
  const [idx, setIdx] = useState(0);
  const answers = session.answers || {};
  const submitted = session.submitted || {};
  const q = QUESTIONS[idx];
  const value = answers[q.id] ?? (q.type === "choice" ? null : "");
  const isSubmitted = !!submitted[q.id];

  const saveAnswer = (newValue) => {
    update({ answers: { ...answers, [q.id]: newValue } });
  };

  const submit = () => {
    update({ submitted: { ...submitted, [q.id]: true } });
  };

  // Only restore position on initial mount, not on every submission
  useEffect(() => {
    const firstUnsubmitted = QUESTIONS.findIndex((qq) => !submitted[qq.id]);
    if (firstUnsubmitted >= 0) setIdx(firstUnsubmitted);
    // eslint-disable-next-line
  }, []);

  const next = () => {
    if (idx < QUESTIONS.length - 1) setIdx(idx + 1);
    else goTo("herQuestions");
  };
  const back = () => {
    if (idx > 0) setIdx(idx - 1);
    else goTo("intro");
  };

  const hasAnswer =
    q.type === "choice" ? !!value : (value || "").toString().trim().length > 0;
  const canSubmit = q.type === "song" ? !!parseMediaUrl(value) : hasAnswer;

  return (
    <div className="scene">
      <div
        style={{
          height: 3,
          background: "var(--line)",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${((idx + 1) / QUESTIONS.length) * 100}%`,
            background: "var(--accent)",
            transition: "width 0.4s ease",
          }}
        />
      </div>
      <p className="tagline" style={{ fontSize: "0.9rem", marginBottom: 0 }}>
        {idx + 1} / {QUESTIONS.length}
      </p>

      <h2 style={{ marginBottom: 4 }}>{q.prompt}</h2>

      {q.type === "text" && (
        <input
          type="text"
          value={value}
          placeholder={q.placeholder || ""}
          disabled={isSubmitted}
          onChange={(e) => saveAnswer(e.target.value)}
        />
      )}

      {q.type === "long" && (
        <textarea
          value={value}
          placeholder={q.placeholder || ""}
          rows={4}
          disabled={isSubmitted}
          onChange={(e) => saveAnswer(e.target.value)}
        />
      )}

      {q.type === "song" && (
        <>
          <input
            type="text"
            value={value}
            placeholder={q.placeholder || ""}
            disabled={isSubmitted}
            onChange={(e) => saveAnswer(e.target.value)}
          />
          <MediaEmbed url={value} />
        </>
      )}

      {q.type === "choice" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {q.options.map((opt) => {
            const selected = value === opt;
            return (
              <button
                key={opt}
                type="button"
                disabled={isSubmitted}
                onClick={() => saveAnswer(opt)}
                style={{
                  textAlign: "left",
                  padding: "12px 16px",
                  background: selected
                    ? "rgba(193,68,88,0.18)"
                    : "rgba(28,20,34,0.7)",
                  border: `1px solid ${
                    selected ? "var(--accent)" : "var(--line)"
                  }`,
                  borderRadius: 2,
                  color: "var(--ink)",
                  fontFamily: "var(--sans)",
                  fontSize: "1rem",
                  cursor: isSubmitted ? "default" : "pointer",
                  opacity: isSubmitted && !selected ? 0.5 : 1,
                  transition: "all 0.15s ease",
                  backdropFilter: "blur(6px)",
                  WebkitBackdropFilter: "blur(6px)",
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}

      {!isSubmitted && (
        <div style={{ marginTop: 16 }}>
          <button
            className="btn"
            onClick={submit}
            disabled={!canSubmit}
            style={{
              opacity: canSubmit ? 1 : 0.4,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            enter
          </button>
        </div>
      )}

      {isSubmitted && (
        <div
          style={{
            marginTop: 24,
            padding: "20px 22px",
            border: "1px solid var(--accent-2)",
            borderRadius: 4,
            background: "rgba(142,111,184,0.08)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            animation: "fadeIn 0.6s ease both",
          }}
        >
          <p
            className="tagline"
            style={{
              fontSize: "0.9rem",
              marginBottom: 8,
              color: "var(--accent-2)",
              textTransform: "uppercase",
              letterSpacing: "2px",
            }}
          >
            ↓ here's how i answered
          </p>
          {q.type === "song" ? (
            <MediaEmbed url={q.myAnswer} compact />
          ) : (
            <p
              style={{
                fontFamily: "var(--serif)",
                fontStyle: "italic",
                fontSize: "1.15rem",
                lineHeight: 1.5,
                color: "var(--ink)",
                margin: 0,
              }}
            >
              {q.myAnswer}
            </p>
          )}
        </div>
      )}

      <div
        className="row"
        style={{ justifyContent: "space-between", marginTop: 20 }}
      >
        <button className="btn btn-ghost" onClick={back}>
          back
        </button>
        {isSubmitted && (
          <button className="btn" onClick={next}>
            {idx === QUESTIONS.length - 1 ? "your turn →" : "next"}
          </button>
        )}
      </div>
    </div>
  );
}
